import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Globe, Video, Clock, X } from 'lucide-react';
import { City } from '@/data/cities';
import { CityLanguage } from '@/context/languages/languageData';

interface CityQuickFiltersProps {
  city: City;
  language: CityLanguage;
  onFilterChange?: (filters: FilterState) => void;
}

interface FilterState {
  specialty: string;
  language: string;
  showOnline: boolean;
  showAvailable: boolean;
}

const CityQuickFilters: React.FC<CityQuickFiltersProps> = ({ city, language, onFilterChange }) => {
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [showOnline, setShowOnline] = useState(false);
  const [showAvailable, setShowAvailable] = useState(false);

  const handleFilterChange = () => {
    if (onFilterChange) {
      onFilterChange({
        specialty: selectedSpecialty,
        language: selectedLanguage,
        showOnline,
        showAvailable
      });
    }
  };

  const clearFilters = () => {
    setSelectedSpecialty('');
    setSelectedLanguage('');
    setShowOnline(false);
    setShowAvailable(false);
    if (onFilterChange) {
      onFilterChange({
        specialty: '',
        language: '',
        showOnline: false,
        showAvailable: false
      });
    }
  };

  const hasActiveFilters = selectedSpecialty || selectedLanguage || showOnline || showAvailable;

  return (
    <div className="bg-background py-6">
      <Card className="max-w-4xl mx-auto border-border shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Filtro de especialidad */}
            <Select value={selectedSpecialty} onValueChange={(value) => {
              setSelectedSpecialty(value);
              handleFilterChange();
            }}>
              <SelectTrigger className="w-full sm:w-48 bg-background border-border">
                <Search className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Specialty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="anxiety">Anxiety Therapy</SelectItem>
                <SelectItem value="depression">Depression Treatment</SelectItem>
                <SelectItem value="couples">Couples Therapy</SelectItem>
                <SelectItem value="family">Family Therapy</SelectItem>
                <SelectItem value="trauma">Trauma & PTSD</SelectItem>
                <SelectItem value="addiction">Addiction Recovery</SelectItem>
                <SelectItem value="stress">Stress Management</SelectItem>
                <SelectItem value="grief">Grief Counseling</SelectItem>
              </SelectContent>
            </Select>

            {/* Filtro de idioma */}
            <Select value={selectedLanguage} onValueChange={(value) => {
              setSelectedLanguage(value);
              handleFilterChange();
            }}>
              <SelectTrigger className="w-full sm:w-40 bg-background border-border">
                <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="it">Italiano</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
              </SelectContent>
            </Select>

            {/* Badges de filtros rápidos */}
            <div className="flex gap-2 flex-wrap">
              <Badge 
                variant={showOnline ? "default" : "outline"} 
                className="cursor-pointer hover:bg-primary/90 transition-colors"
                onClick={() => {
                  setShowOnline(!showOnline);
                  handleFilterChange();
                }}
              >
                <Video className="h-3 w-3 mr-1" />
                Online
              </Badge>
              <Badge 
                variant={showAvailable ? "default" : "outline"}
                className="cursor-pointer hover:bg-primary/90 transition-colors"
                onClick={() => {
                  setShowAvailable(!showAvailable);
                  handleFilterChange();
                }}
              >
                <Clock className="h-3 w-3 mr-1" />
                Available Now
              </Badge>
            </div>

            {/* Botón de reset */}
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearFilters}
                className="ml-auto"
              >
                <X className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Contador de resultados */}
          <p className="text-sm text-muted-foreground mt-4">
            Showing {hasActiveFilters ? '47' : '150+'} therapists in {city.name.replace(/\s+\d+$/, '').trim()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CityQuickFilters;
